import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

// Correctif pour la galerie personnalisée Modélia.
document.addEventListener('click', (event) => {
  const thumbnail = event.target instanceof Element ? event.target.closest('[data-modelia-thumb]') : null;
  if (!(thumbnail instanceof HTMLElement)) return;

  const root = thumbnail.closest('[data-modelia-product]');
  const mainImage = root?.querySelector('.modelia-buy__main img');
  const source = thumbnail.dataset.src;
  if (!(mainImage instanceof HTMLImageElement) || !source) return;

  root.querySelectorAll('[data-modelia-thumb]').forEach((item) => item.classList.remove('is-active'));
  thumbnail.classList.add('is-active');

  mainImage.style.opacity = '0';
  window.setTimeout(() => {
    mainImage.removeAttribute('srcset');
    mainImage.removeAttribute('sizes');
    mainImage.src = source;
    mainImage.alt = thumbnail.dataset.alt || '';
    mainImage.style.opacity = '1';
  }, 110);
});

const applyModeliaCopy = () => {
  const productRoot = document.querySelector('[data-modelia-product]');
  if (productRoot) {
    const title = productRoot.querySelector('h1')?.textContent?.trim().toLowerCase() || '';
    const proof = productRoot.querySelector('.modelia-buy__proof-label');
    const benefits = [...productRoot.querySelectorAll('.modelia-buy__benefits li div')];

    let copy = null;
    if (title.includes('menstru')) {
      copy = {
        proof: 'Pensée pour une protection sereine',
        benefits: [
          'Aide à absorber les flux menstruels pour plus de sérénité au quotidien',
          'Fibre de bambou douce et respirante pour un confort prolongé',
          'Coupe couvrante conçue pour rester discrète sous les vêtements',
        ],
      };
    } else if (title.includes('silhou') || title.includes('short gainant')) {
      copy = {
        proof: 'Pensé pour lisser la silhouette',
        benefits: [
          'Lisse le ventre, les hanches et le haut des cuisses sous les vêtements',
          'Conception sans couture pour un rendu plus discret',
          'Matière extensible qui accompagne les mouvements sans gêner',
        ],
      };
    }

    if (copy) {
      if (proof) proof.textContent = copy.proof;
      benefits.forEach((item, index) => {
        if (copy.benefits[index]) item.textContent = copy.benefits[index];
      });
    }
  }

  const promiseTitle = document.querySelector('.mh__promise h2');
  if (promiseTitle && promiseTitle.textContent?.includes('fonctionnel')) {
    promiseTitle.textContent = 'Des solutions utiles, pensées pour le quotidien.';
  }

  const heroTitle = document.querySelector('.mh__hero h1');
  if (heroTitle) {
    heroTitle.innerHTML = '<span>La lingerie</span><span>qui travaille</span><span>avec votre corps.</span>';
    heroTitle.querySelectorAll('span').forEach((line) => {
      line.style.display = 'block';
    });
  }

  const heroEyebrow = document.querySelector('.mh__hero .mh__eyebrow');
  if (heroEyebrow) {
    heroEyebrow.style.fontSize = 'clamp(.56rem, 1.8vw, .7rem)';
    heroEyebrow.style.letterSpacing = '.08em';
    heroEyebrow.style.whiteSpace = 'nowrap';
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyModeliaCopy, { once: true });
} else {
  applyModeliaCopy();
}
